import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'
import {
  Captions,
  ChevronLeft,
  ChevronRight,
  Download,
  Gauge,
  Loader2,
  MoreHorizontal,
  Pause,
  Play,
  Sparkles,
} from 'lucide-react'
import type { AudioClip } from '@/lib/types'
import { cn } from '@/lib/utils'

/** Allowed playback rates for the speed menu — kept centralized so the
 *  type can be re-used by parents that need to validate stored prefs. */
export const AUDIO_SPEEDS = [1, 1.25, 1.5, 1.75, 2] as const
export type AudioSpeed = (typeof AUDIO_SPEEDS)[number]
export const DEFAULT_AUDIO_SPEED: AudioSpeed = 1

interface Props {
  /** All audio URLs on the parent ticket (1 or more). */
  urls: string[]
  /** Optional clipping ranges per URL — populated for cards born from a
   *  split, so the player only spans [start_sec, end_sec] of that audio. */
  clipsByUrl?: Map<string, AudioClip>
  /** URLs whose transcription has already been appended to the ticket's
   *  description. The kebab's « Transcrire » entry for these URLs is
   *  relabelled « Déjà transcrit » and disabled. */
  transcribedUrls?: Set<string>
  /** Called when the user picks "Transcrire" from the kebab. Receives
   *  the currently-selected audio URL. Implementation lives in App.tsx
   *  (it looks up the source message, calls the transcribe-audio edge
   *  function, and appends the transcription to the ticket description).
   *  Hide the menu item if not provided. */
  onTranscribe?: (url: string) => void | Promise<void>
  /** Triggered from a new kebab entry « Mettre à jour titre +
   *  description avec l'IA ». The parent is expected to already have a
   *  summary + transcription for this audio (otherwise it doesn't pass
   *  the prop) and pushes them into the ticket's title / description. */
  onApplyAIToCard?: (url: string) => void | Promise<void>
  /** Current playback rate, applied to every audio this player handles.
   *  Sourced from the user's preferences in App.tsx — same value across
   *  every InlineAudio on the board so the user only sets it once. */
  playbackRate?: AudioSpeed
  /** Called when the user picks a different speed in the kebab. Parent
   *  persists it (profiles.preferences.audio_speed) so the choice
   *  survives reloads and propagates to all players. */
  onPlaybackRateChange?: (rate: AudioSpeed) => void
  className?: string
}

/** Slim, in-card audio player — replaces the native `<audio controls>` so
 *  the player blends into the card instead of dominating it.
 *
 *  Features:
 *    - Compact custom UI: round play button, thin progress bar, time.
 *    - Navigation arrows + counter (« 3 / 8 ») when the card has more
 *      than one audio — the user can flip through every audio without
 *      opening a modal.
 *    - Kebab menu with a "Transcrire" action that delegates to a
 *      parent-provided callback. The transcription result is added to
 *      the card description (handled by the parent).
 *    - Per-URL clipping support (same semantics as the old ClippedAudio):
 *      we clamp playback to `[startSec, endSec]` when the URL has a clip.
 */
export function InlineAudio({
  urls,
  clipsByUrl,
  transcribedUrls,
  onTranscribe,
  onApplyAIToCard,
  playbackRate = DEFAULT_AUDIO_SPEED,
  onPlaybackRateChange,
  className,
}: Props) {
  const { t } = useTranslation()
  const [index, setIndex] = useState(0)
  const [playing, setPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [menuOpen, setMenuOpen] = useState(false)
  // Viewport coords for the menu when open. We render the menu via a
  // portal anchored to `document.body` instead of inline under the
  // trigger — otherwise it lives inside the card's stacking context
  // and gets visually clipped by the NEXT card below (a kanban column
  // is just a stack of cards, each its own context).
  const [menuPos, setMenuPos] = useState<{ top: number; right: number } | null>(
    null,
  )
  const [transcribing, setTranscribing] = useState(false)
  const [applyingAI, setApplyingAI] = useState(false)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const menuRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)

  const safeIndex = Math.max(0, Math.min(index, urls.length - 1))
  const url = urls[safeIndex]
  const clip = clipsByUrl?.get(url)
  const clipStart = clip?.start_sec ?? 0
  const clipEnd = clip?.end_sec
  const clipped = clipEnd !== undefined && clipEnd > clipStart

  // Effective playable window — for non-clipped audios, this is just the
  // whole file (duration loaded from metadata). For clipped audios, the
  // window is fixed at construction time.
  const windowStart = clipped ? clipStart : 0
  const windowEnd = clipped ? clipEnd! : duration

  // Wire native audio events to local state.
  useEffect(() => {
    const el = audioRef.current
    if (!el) return

    function onMeta() {
      if (!el) return
      setDuration(el.duration || 0)
      // Seek to clip start if clipped.
      if (clipped) {
        try {
          el.currentTime = clipStart
        } catch {
          /* element may still be initializing */
        }
      }
      // Apply the user's chosen playback rate as soon as the element
      // accepts it (some browsers reset rate to 1× on src change).
      try {
        el.playbackRate = playbackRate
      } catch {
        /* ignore */
      }
    }
    function onTime() {
      if (!el) return
      const t = el.currentTime
      setCurrentTime(t)
      // Auto-stop at clip end and rewind to start so a second play starts
      // at the beginning of the clip rather than past the end.
      if (clipped && t >= clipEnd!) {
        el.pause()
        try {
          el.currentTime = clipStart
        } catch {
          /* ignore */
        }
        setPlaying(false)
      }
    }
    function onPlay() {
      setPlaying(true)
    }
    function onPauseOrEnd() {
      setPlaying(false)
    }
    function onSeek() {
      if (!el) return
      // Clamp scrub to inside the clip window when applicable.
      if (clipped) {
        if (el.currentTime < clipStart) el.currentTime = clipStart
        else if (el.currentTime > clipEnd!) el.currentTime = clipEnd!
      }
    }

    el.addEventListener('loadedmetadata', onMeta)
    el.addEventListener('timeupdate', onTime)
    el.addEventListener('play', onPlay)
    el.addEventListener('pause', onPauseOrEnd)
    el.addEventListener('ended', onPauseOrEnd)
    el.addEventListener('seeking', onSeek)
    if (el.readyState >= 1) onMeta()

    return () => {
      el.removeEventListener('loadedmetadata', onMeta)
      el.removeEventListener('timeupdate', onTime)
      el.removeEventListener('play', onPlay)
      el.removeEventListener('pause', onPauseOrEnd)
      el.removeEventListener('ended', onPauseOrEnd)
      el.removeEventListener('seeking', onSeek)
    }
  }, [url, clipped, clipStart, clipEnd])

  // When the user switches audios via arrows, reset playback state.
  useEffect(() => {
    setPlaying(false)
    setCurrentTime(0)
    setDuration(0)
  }, [url])

  // Apply playback-rate changes LIVE so picking 1.5× from the kebab
  // while the audio is playing speeds it up immediately, without
  // requiring a reload of the source.
  useEffect(() => {
    const el = audioRef.current
    if (!el) return
    try {
      el.playbackRate = playbackRate
    } catch {
      /* ignore */
    }
  }, [playbackRate])

  // Close the kebab on outside click / Escape.
  useEffect(() => {
    if (!menuOpen) return
    function onDown(e: MouseEvent) {
      const tgt = e.target as Node
      if (menuRef.current?.contains(tgt)) return
      if (triggerRef.current?.contains(tgt)) return
      setMenuOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [menuOpen])

  // (Re)compute the portalled menu's viewport position whenever it
  // opens, and keep it pinned to the trigger if the user scrolls /
  // resizes the window while it's open.
  useEffect(() => {
    if (!menuOpen) return
    function place() {
      const r = triggerRef.current?.getBoundingClientRect()
      if (!r) return
      // Anchor by the trigger's BOTTOM-RIGHT corner: menu hangs down
      // and aligns its right edge with the trigger. `right` is the
      // distance from the viewport right edge (= window.innerWidth - r.right)
      // so we can use it directly with CSS `right:` for positioning.
      setMenuPos({ top: r.bottom + 4, right: window.innerWidth - r.right })
    }
    place()
    window.addEventListener('scroll', place, true)
    window.addEventListener('resize', place)
    return () => {
      window.removeEventListener('scroll', place, true)
      window.removeEventListener('resize', place)
    }
  }, [menuOpen])

  function togglePlay() {
    const el = audioRef.current
    if (!el) return
    if (el.paused) el.play().catch(() => {})
    else el.pause()
  }

  function seekTo(t: number) {
    const el = audioRef.current
    if (!el) return
    el.currentTime = t
    setCurrentTime(t)
  }

  function onProgressBarClick(e: React.MouseEvent<HTMLDivElement>) {
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = (e.clientX - rect.left) / rect.width
    const span = windowEnd - windowStart
    seekTo(windowStart + Math.max(0, Math.min(1, ratio)) * span)
  }

  async function handleTranscribe() {
    setMenuOpen(false)
    if (!onTranscribe || transcribing) return
    setTranscribing(true)
    try {
      await onTranscribe(url)
    } finally {
      setTranscribing(false)
    }
  }

  async function handleApplyAI() {
    setMenuOpen(false)
    if (!onApplyAIToCard || applyingAI) return
    setApplyingAI(true)
    try {
      await onApplyAIToCard(url)
    } finally {
      setApplyingAI(false)
    }
  }

  const hasMany = urls.length > 1
  // Position the cursor along the clip window, not the whole file.
  const displayedTime = clipped ? Math.max(0, currentTime - clipStart) : currentTime
  const displayedDuration = clipped ? clipEnd! - clipStart : duration
  const progressPct = displayedDuration
    ? Math.max(0, Math.min(100, (displayedTime / displayedDuration) * 100))
    : 0

  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg border border-[var(--color-border-soft)] bg-[var(--color-surface-2)]/60 px-2 py-1.5',
        className,
      )}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Hidden native audio element — drives state, never rendered. */}
      <audio ref={audioRef} src={url} preload="metadata" className="hidden" />

      {/* Multi-audio navigator: « 3 / 8 » with prev/next arrows. */}
      {hasMany && (
        <div className="flex shrink-0 items-center gap-0.5 text-[10px] tabular-nums text-[var(--color-text-dim)]">
          <button
            type="button"
            aria-label={t('audio.previous')}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={safeIndex === 0}
            className="inline-flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)] disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <span className="min-w-[2.2em] text-center">
            {safeIndex + 1}/{urls.length}
          </span>
          <button
            type="button"
            aria-label={t('audio.next')}
            onClick={() =>
              setIndex((i) => Math.min(urls.length - 1, i + 1))
            }
            disabled={safeIndex === urls.length - 1}
            className="inline-flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)] disabled:opacity-30 disabled:hover:bg-transparent"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Play / pause button. */}
      <button
        type="button"
        aria-label={playing ? t('audio.pause') : t('audio.play')}
        onClick={togglePlay}
        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent)]/15 text-[var(--color-accent)] transition-colors hover:bg-[var(--color-accent)]/25"
      >
        {playing ? (
          <Pause className="h-3 w-3" fill="currentColor" />
        ) : (
          <Play className="h-3 w-3 translate-x-[0.5px]" fill="currentColor" />
        )}
      </button>

      {/* Progress bar — click anywhere to seek. */}
      <div
        role="slider"
        aria-label="Position dans la lecture"
        aria-valuenow={Math.round(displayedTime)}
        aria-valuemax={Math.round(displayedDuration)}
        tabIndex={0}
        onClick={onProgressBarClick}
        className="group relative h-1 min-w-0 flex-1 cursor-pointer rounded-full bg-[var(--color-border-soft)]"
      >
        <div
          className="h-full rounded-full bg-[var(--color-accent)]/70 transition-[width] duration-100"
          style={{ width: `${progressPct}%` }}
        />
        {/* Thumb appears on hover for a more "scrubable" feel. */}
        <div
          className="absolute top-1/2 h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--color-accent)] opacity-0 transition-opacity group-hover:opacity-100"
          style={{ left: `${progressPct}%` }}
        />
      </div>

      {/* Time readout — current / total (mm:ss). */}
      <span className="shrink-0 font-mono text-[10px] tabular-nums text-[var(--color-text-dim)]">
        {fmtTime(displayedTime)}/{fmtTime(displayedDuration)}
      </span>

      {/* Kebab trigger — the menu itself is portalled below so it can
          float over the next cards in the column without being clipped
          by their stacking contexts. */}
      <div className="shrink-0">
        <button
          ref={triggerRef}
          type="button"
          aria-label={t('audio.menu')}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((v) => !v)}
          className="inline-flex h-5 w-5 items-center justify-center rounded text-[var(--color-text-dim)] transition-colors hover:bg-[var(--color-surface-3)] hover:text-[var(--color-text)]"
        >
          <MoreHorizontal className="h-3.5 w-3.5" />
        </button>
      </div>
      {menuOpen &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ top: menuPos.top, right: menuPos.right }}
            className="fixed z-[80] w-44 overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] py-1 text-[12px] shadow-[var(--shadow-modal)]"
          >
            {onTranscribe && (() => {
              const alreadyDone = transcribedUrls?.has(url) === true
              const disabled = transcribing || alreadyDone
              return (
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleTranscribe}
                  disabled={disabled}
                  title={alreadyDone ? t('audio.alreadyTranscribed') : undefined}
                  className={cn(
                    'flex w-full items-center gap-2 px-3 py-1.5 text-left transition-colors',
                    alreadyDone
                      ? 'cursor-default text-[var(--color-text-dim)]/50'
                      : 'text-[var(--color-text)] hover:bg-[var(--color-surface-2)]',
                    transcribing && 'cursor-progress opacity-60',
                  )}
                >
                  {transcribing ? (
                    <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-[var(--color-text-dim)]" />
                  ) : (
                    <Captions
                      className={cn(
                        'h-3.5 w-3.5 shrink-0',
                        alreadyDone
                          ? 'text-[var(--color-text-dim)]/50'
                          : 'text-[var(--color-text-dim)]',
                      )}
                    />
                  )}
                  {alreadyDone ? t('audio.alreadyTranscribed') : t('audio.transcribe')}
                </button>
              )
            })()}
            {onApplyAIToCard && (
              <button
                type="button"
                role="menuitem"
                onClick={handleApplyAI}
                disabled={applyingAI}
                title={t('detail.rewriteWithAi')}
                className={cn(
                  'flex w-full items-center gap-2 px-3 py-1.5 text-left text-[var(--color-text)] transition-colors hover:bg-[var(--color-surface-2)]',
                  applyingAI && 'cursor-progress opacity-60',
                )}
              >
                {applyingAI ? (
                  <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-[var(--color-text-dim)]" />
                ) : (
                  <Sparkles className="h-3.5 w-3.5 shrink-0 text-[var(--color-link)]" />
                )}
                {t('audio.rewriteWithAi')}
              </button>
            )}
            {onPlaybackRateChange && (
              <>
                <div className="my-0.5 border-t border-[var(--color-border-soft)]" />
                <div className="px-3 pb-1 pt-1.5 text-[10px] font-medium uppercase tracking-wide text-[var(--color-text-dim)]">
                  <span className="inline-flex items-center gap-1.5">
                    <Gauge className="h-3 w-3" />
                    {t('audio.playbackSpeed')}
                  </span>
                </div>
                <div className="grid grid-cols-5 gap-0.5 px-1.5 pb-1">
                  {AUDIO_SPEEDS.map((rate) => {
                    const active = rate === playbackRate
                    return (
                      <button
                        key={rate}
                        type="button"
                        role="menuitemradio"
                        aria-checked={active}
                        onClick={() => {
                          onPlaybackRateChange(rate)
                          setMenuOpen(false)
                        }}
                        className={cn(
                          'rounded-md px-1 py-1 text-[11px] tabular-nums transition-colors',
                          active
                            ? 'bg-[var(--color-accent)]/15 font-semibold text-[var(--color-accent)]'
                            : 'text-[var(--color-text)] hover:bg-[var(--color-surface-2)]',
                        )}
                      >
                        {rate}×
                      </button>
                    )
                  })}
                </div>
                <div className="my-0.5 border-t border-[var(--color-border-soft)]" />
              </>
            )}
            <a
              role="menuitem"
              href={url}
              download
              onClick={() => setMenuOpen(false)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[var(--color-text)] hover:bg-[var(--color-surface-2)]"
            >
              <Download className="h-3.5 w-3.5 shrink-0 text-[var(--color-text-dim)]" />
              {t('audio.download')}
            </a>
          </div>,
          document.body,
        )}
    </div>
  )
}

function fmtTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00'
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}
